import { Input } from '@app/app/_components/Input/Input';
import { SelectItem } from '@app/app/_components/Input/Select/SelectItem';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { SearchIcon } from '@app/app/_icons/SearchIcon';
import { useTranslations } from 'next-intl';
import { useCallback, KeyboardEvent, forwardRef, Ref, ChangeEvent, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useFontSize } from '@app/app/_context/AccessibilityContext/useFontSize';
import { FontSize } from '@app/domain/model/FontSize';
import { useIsMobile } from '@app/infrastructure/application/useIsMobile';

interface Props {
    options: SelectOption[];
    placeholder?: string;
    selectedOption?: SelectOption;
    highlightedIndex?: number;
    onOptionClick?: (option: SelectOption) => void;
    onDismiss?: () => void;
    onNavigate?: (e: KeyboardEvent<HTMLElement>) => void;
    id?: string;
    searchable?: boolean;
    onSearchChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    hint?: string;
    bottomAddon?: ReactNode;
    noOverlayOnMobile?: boolean;
    className?: string;
}

export const SelectMenu = forwardRef(function SelectMenuWithRef(
    {
        options,
        placeholder,
        selectedOption,
        highlightedIndex = -1,
        onOptionClick,
        onDismiss,
        onNavigate,
        id,
        searchable,
        onSearchChange,
        hint,
        bottomAddon,
        noOverlayOnMobile = false,
        className,
    }: Props,
    ref: Ref<HTMLUListElement>,
) {
    const t = useTranslations();

    const fontSize = useFontSize();
    const bigFontSizes = [FontSize.ExtraLarge, FontSize.Huge];
    const isBigFontSize = bigFontSizes.map(size => size.toString()).includes(fontSize);
    const isMobile = useIsMobile();

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLUListElement>) => {
            if (e.key === 'Escape') {
                onDismiss?.();

                return;
            }

            onNavigate?.(e);
        },
        [onDismiss, onNavigate],
    );

    return (
        <>
            {!noOverlayOnMobile && (isBigFontSize || isMobile) && (
                <div className="fixed inset-0 z-10 block bg-black opacity-45" />
            )}

            <ul
                ref={ref}
                className={twMerge(
                    'fixed inset-x-0 bottom-0 z-20 flex select-none flex-col overflow-hidden rounded-t-lg bg-white animate-in slide-in-from-bottom-full max-sm:min-h-[60vh]',
                    !isMobile &&
                        !isBigFontSize &&
                        'md:absolute md:bottom-auto md:top-12 md:max-w-prose md:rounded-lg md:shadow-lg md:animate-in md:fade-in md:slide-in-from-top-10',
                    className,
                )}
                onKeyDown={handleKeyDown}
                role="listbox"
                id={id}>
                <div
                    className={twMerge(
                        'flex items-center justify-between gap-2 border-b border-b-neutral-100 px-6 py-3',
                        !isBigFontSize && !isMobile && 'md:hidden',
                    )}>
                    <div className="text-2xl font-medium">{placeholder}</div>
                    <button className="p-1" onClick={onDismiss}>
                        <CloseIcon className="size-6 text-neutral-500" />
                    </button>
                </div>
                {searchable && (
                    <Input
                        className={twMerge('mb-3 flex px-6', !isBigFontSize && !isMobile && 'md:hidden')}
                        placeholder={t('general.search')}
                        onChange={onSearchChange}
                        suffix={<SearchIcon className="size-4" />}
                    />
                )}
                <div
                    className={twMerge(
                        'flex max-h-[60vh] flex-col gap-1 overflow-y-auto overflow-x-hidden bg-neutral-50 px-6 py-6 max-sm:min-h-[60vh]',
                        !isBigFontSize && !isMobile && 'md:max-h-[45vh] md:gap-0 md:p-1',
                    )}>
                    {options.map((option, index) => (
                        <SelectItem
                            key={option.value}
                            id={id != null ? `${id}-option-${index}` : undefined}
                            option={option}
                            selected={option.value === selectedOption?.value}
                            highlighted={index === highlightedIndex}
                            focusable={!searchable}
                            onClick={onOptionClick}>
                            {option.label}
                        </SelectItem>
                    ))}

                    {hint != null && (
                        <div
                            className={twMerge(
                                'py-2.5 text-sm text-neutral-400',
                                !isBigFontSize && !isMobile && 'md:px-3 md:py-1.25',
                            )}>
                            {hint}
                        </div>
                    )}

                    {bottomAddon != null && bottomAddon}
                </div>
            </ul>
        </>
    );
});
